import { useContext, useRef, useState } from "react";
import TenantContext from "../../context/tenant/TenantContext";
import { haveValue, toast } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";

const MAX_FILE_SIZE = 8 * 1024 * 1024;

const TenantChecksIncomeModel = ({ user, formHandler }) => {
  const { _t } = useTranslator();
  const { uploadBankStatement } = useContext(TenantContext);
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const reCheck = new URLSearchParams(window.location.search).get("reCheck");

  const stageFile = (file) => {
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error(_t("fileTypeNotAllowed"));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        _t("fileUploadedExceedsEightMbFileLimit", "Files can be up to 8 MB")
      );
      return;
    }

    setPendingFile(file);
  };

  const handleSubmit = async () => {
    if (!pendingFile || !user?._id) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", pendingFile);
      formData.append("tenant_id", user._id);
      await uploadBankStatement(formData);
      toast.success(_t("bankStatementAdded"));
      setPendingFile(null);

      if (haveValue(reCheck) && reCheck) {
        window.history.back();
      } else {
        await formHandler.next();
      }
    } catch (error) {
      // uploadBankStatement owns the user-facing API error message.
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="popup-overlay wrapper-modal"
      style={{
        alignItems: "flex-start",
        background: "var(--skor-color-canvas, #f6f6f6)",
        display: "flex",
        justifyContent: "center",
        overflowY: "auto",
        padding: "0 16px",
      }}
    >
      <section
        style={{
          background: "#fff",
          border: "1px solid var(--skor-color-border-card, #e8e8ef)",
          borderRadius: 18,
          boxShadow: "0 18px 50px rgba(37, 56, 55, 0.09)",
          margin: "clamp(32px, 7vh, 80px) 0 clamp(24px, 3.5vh, 40px)",
          padding: "20px 22px 22px",
          width: "min(560px, 100%)",
        }}
      >
        {uploading ? (
          <div
            aria-live="polite"
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              padding: "32px 0 18px",
              textAlign: "center",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                animation: "tenant-check-spin 0.9s linear infinite",
                border: "3px solid #1a2e2a",
                borderRadius: "50%",
                borderTopColor: "transparent",
                height: 36,
                width: 36,
              }}
            />
            <style>{`@keyframes tenant-check-spin { to { transform: rotate(360deg); } }`}</style>
            <h2
              style={{ color: "#1a2e2a", fontSize: 20, margin: "18px 0 6px" }}
            >
              {_t("analyzingYourStatement", "Analyzing your statement")}
            </h2>
            <p style={{ color: "#5a5a6a", fontSize: 13, margin: 0 }}>
              {_t(
                "analyzingSubtitle",
                "A few seconds. Don't close this window."
              )}
            </p>
            <div
              style={{
                alignItems: "flex-start",
                color: "#1a2e2a",
                display: "flex",
                flexDirection: "column",
                fontSize: 13,
                fontWeight: 600,
                gap: 8,
                marginTop: 24,
              }}
            >
              <span>✓ {_t("readingTheDocument", "Reading the document")}</span>
              <span>
                ✓ {_t("checkingItsInEuros", "Checking it's in euros")}
              </span>
              <span style={{ color: "#5a5a6a" }}>
                ◌{" "}
                {_t(
                  "checkingItCoversLast3Months",
                  "Checking it covers the last 3 months"
                )}
              </span>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() =>
                haveValue(reCheck) && reCheck
                  ? window.history.back()
                  : formHandler.prev()
              }
              style={{
                alignItems: "center",
                background: "none",
                border: 0,
                color: "#1a2e2a",
                cursor: "pointer",
                display: "flex",
                fontSize: 13,
                fontWeight: 600,
                gap: 6,
                marginBottom: 14,
              }}
            >
              <span aria-hidden="true">‹</span> {_t("back", "Back")}
            </button>
            <h1
              style={{
                color: "#1a2e2a",
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1.2,
                margin: "0 0 6px",
              }}
            >
              {_t("uploadTheirBankStatement", "Upload their bank statement")}
            </h1>
            <p
              style={{
                color: "#5a5a6a",
                fontSize: 13,
                lineHeight: 1.45,
                margin: 0,
              }}
            >
              {_t(
                "uploadTheirBankStatementSubtitle",
                "Upload a PDF bank statement covering the last 3 months"
              )}
            </p>

            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                marginTop: 20,
                minHeight: 220,
              }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => !pendingFile && fileRef.current?.click()}
                onKeyDown={(event) => {
                  if (
                    !pendingFile &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    fileRef.current?.click();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragOver(false);
                  stageFile(event.dataTransfer.files?.[0]);
                }}
                style={{
                  alignItems: "center",
                  background: dragOver ? "#e7f4d8" : "#f1f8e8",
                  border: `1.5px dashed ${dragOver ? "#86ad63" : "#cfe8b8"}`,
                  borderRadius: 18,
                  cursor: pendingFile ? "default" : "pointer",
                  display: "flex",
                  justifyContent: "center",
                  minHeight: 132,
                  padding: 16,
                  transition: "background 120ms ease, border-color 120ms ease",
                  width: "min(380px, 100%)",
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => {
                    stageFile(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                  style={{ display: "none" }}
                />
                {pendingFile ? (
                  <div
                    style={{
                      alignItems: "center",
                      background: "#fff",
                      borderRadius: 999,
                      display: "flex",
                      gap: 12,
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      width: "100%",
                    }}
                  >
                    <span
                      title={pendingFile.name}
                      style={{
                        color: "#1a2e2a",
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pendingFile.name} ·{" "}
                      {(pendingFile.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      aria-label={_t("removeFile", "Remove file")}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingFile(null);
                      }}
                      style={{
                        background: "none",
                        border: 0,
                        color: "#6b7280",
                        cursor: "pointer",
                        fontSize: 20,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "#1a5e1a", fontSize: 28 }}>↑</div>
                    <strong style={{ color: "#1a2e2a", fontSize: 14 }}>
                      {_t("uploadYourStatement", "Upload your statement")}
                    </strong>
                    <div
                      style={{ color: "#5a5a6a", fontSize: 12, marginTop: 4 }}
                    >
                      {_t(
                        "bankStatementFileHint",
                        "Drag it here or click to select · PDF · up to 8 MB"
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  pendingFile ? handleSubmit() : fileRef.current?.click()
                }
                style={{
                  background: "#1a2e2a",
                  border: 0,
                  borderRadius: 14,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 700,
                  padding: 14,
                  width: "min(380px, 100%)",
                }}
              >
                {pendingFile
                  ? _t("analyzeStatement", "Analyze statement")
                  : _t("selectFile", "Select file")}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default TenantChecksIncomeModel;
